import { Dumbbell, Play } from "lucide-react";
import type { StudentWorkoutItem } from "@/lib/types";
import { gifPublicUrl } from "./workoutDetail";
import { youTubeThumbUrl } from "@/lib/youtube";

export function itemHasMedia(item: StudentWorkoutItem): boolean {
  return (
    !!item.video_url ||
    !!item.gif_path ||
    (Array.isArray(item.image_urls) && item.image_urls.length > 0)
  );
}

/**
 * Miniatura do exercício. Prioridade: gif > 1ª imagem > poster do vídeo.
 * Sem mídia → ícone de halter. Quando há mídia e `onClick`, vira botão com
 * overlay de play (abre o modal de demonstração). Portado do ExerciseThumb do
 * legado `WorkoutTab.tsx`.
 */
export function ExerciseThumb({
  item,
  size = "medium",
  onClick,
}: {
  item: StudentWorkoutItem;
  size?: "small" | "medium";
  onClick?: () => void;
}) {
  const sizeClass = size === "small" ? "h-16 w-16" : "h-16 w-16 sm:h-20 sm:w-20";
  const box = `relative overflow-hidden rounded-lg border border-border-subtle bg-elevated ${sizeClass}`;
  const has = itemHasMedia(item);

  let inner: React.ReactNode;
  if (!has) {
    inner = (
      <div className="flex h-full w-full items-center justify-center" aria-label="Sem mídia">
        <Dumbbell size={22} className="text-faint opacity-60" aria-hidden />
      </div>
    );
  } else {
    const gifSrc = gifPublicUrl(item.gif_path);
    const imgSrc = item.image_urls?.[0];
    // Link do YouTube não renderiza em <video> (ficaria um quadro vazio) — a
    // miniatura usa o thumbnail do próprio vídeo. Arquivo de vídeo (upload
    // legado) segue no <video> com preload=metadata como poster.
    const ytThumb = youTubeThumbUrl(item.video_url);
    if (gifSrc) {
      inner = <img src={gifSrc} alt={item.line} loading="lazy" className="h-full w-full object-cover" />;
    } else if (imgSrc) {
      inner = <img src={imgSrc} alt={item.line} loading="lazy" className="h-full w-full object-cover" />;
    } else if (ytThumb) {
      inner = <img src={ytThumb} alt={item.line} loading="lazy" className="h-full w-full object-cover" />;
    } else {
      inner = (
        <video
          src={item.video_url ?? undefined}
          muted
          playsInline
          preload="metadata"
          className="h-full w-full object-cover"
          aria-label={item.line}
        />
      );
    }
  }

  if (has && onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${box} group cursor-pointer transition-colors hover:border-border-strong`}
        aria-label={`Ver demonstração de ${item.line}`}
      >
        {inner}
        <span className="absolute inset-0 flex items-center justify-center bg-black/25 opacity-0 transition-opacity group-hover:opacity-100">
          <Play size={20} className="text-ink" aria-hidden />
        </span>
      </button>
    );
  }

  return <div className={box}>{inner}</div>;
}
